/**
 * Store Zustand pour l'administration des utilisateurs
 *
 * Ce store delegue toute la logique metier aux Use Cases
 * et ne gere que l'etat React (state management).
 */

import { create, type StateCreator } from 'zustand';
import { userAdminContainer } from '../../infrastructure/di';
import type {
  UserAdminDTO,
  UserAdminStatsDTO,
  UserFiltersState,
  MettreAJourUtilisateurData,
  UserRole,
} from '../../application/dto/dtos';

/**
 * Interface du store d'administration utilisateurs
 */
export interface UserAdminState {
  // Etat
  users: UserAdminDTO[];
  stats: UserAdminStatsDTO | null;
  loading: boolean;
  statsLoading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;

  // Filtres
  filters: UserFiltersState;

  // Actions - Chargement
  fetchUsers: () => Promise<void>;
  fetchStats: () => Promise<void>;

  // Actions - CRUD
  deleteUser: (idUtilisateur: string) => Promise<void>;
  updateUser: (idUtilisateur: string, data: MettreAJourUtilisateurData) => Promise<void>;

  // Actions - Gestion
  changeRole: (idUtilisateur: string, role: UserRole) => Promise<void>;
  toggleActive: (idUtilisateur: string, activate: boolean) => Promise<void>;
  lockUser: (idUtilisateur: string, dureeMinutes?: number) => Promise<void>;
  unlockUser: (idUtilisateur: string) => Promise<void>;

  // Actions - Export
  exportUsers: () => Promise<void>;

  // Actions - Navigation / Filtres
  setCurrentPage: (page: number) => void;
  setFilters: (filters: UserFiltersState) => void;
  resetFilters: () => void;

  // Actions - Utilitaires
  setError: (error: string | null) => void;
  clearError: () => void;
}

const initialFilters: UserFiltersState = {
  searchTerm: '',
  role: '',
  isActive: '',
  deuxFacteursActive: '',
  lockedAccount: '',
};

const storeImplementation: StateCreator<UserAdminState> = (set, get) => ({
  // Etat initial
  users: [],
  stats: null,
  loading: false,
  statsLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 20,
  filters: { ...initialFilters },

  fetchUsers: async () => {
    const state = get();
    set({ loading: true, error: null });
    try {
      const result = await userAdminContainer.listerUtilisateursUseCase.execute({
        page: state.currentPage,
        limit: state.itemsPerPage,
        searchTerm: state.filters.searchTerm || undefined,
        role: state.filters.role || undefined,
        isActive: state.filters.isActive || undefined,
        deuxFacteursActive: state.filters.deuxFacteursActive || undefined,
        lockedAccount: state.filters.lockedAccount || undefined,
      });
      console.log('userAdminContainer.listerUtilisateursUseCase  resultat',result)
      set({
        users: result.donnees.utilisateurs,
        totalPages: result.donnees.pagination.totalPages,
        totalItems: result.donnees.pagination.total,
        loading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      set({ error: message, loading: false });
    }
  },

  fetchStats: async () => {
    set({ statsLoading: true });
    try {
      const stats = await userAdminContainer.obtenirStatistiquesUseCase.execute();
      set({ stats, statsLoading: false });
    } catch {
      set({ statsLoading: false });
    }
  },

  deleteUser: async (idUtilisateur: string) => {
    const previousUsers = get().users;
    // Suppression optimiste : l'utilisateur disparaît immédiatement de l'UI
    set({ users: previousUsers.filter(u => u.idUtilisateur !== idUtilisateur) });
    try {
      await userAdminContainer.supprimerUtilisateurUseCase.execute({ idUtilisateur });
      await get().fetchUsers();
      await get().fetchStats();
    } catch (error) {
      // Rollback si l'API échoue
      set({ users: previousUsers });
      const message = error instanceof Error ? error.message : 'Erreur suppression';
      set({ error: message });
      throw error;
    }
  },

  updateUser: async (idUtilisateur: string, data: MettreAJourUtilisateurData) => {
    try {
      await userAdminContainer.mettreAJourUtilisateurUseCase.execute({ idUtilisateur, data });
      await get().fetchUsers();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur modification';
      set({ error: message });
      throw error;
    }
  },

  changeRole: async (idUtilisateur: string, role: UserRole) => {
    try {
      // role ici est un idRole (string ID de la DB)
      await userAdminContainer.changerRoleUseCase.execute({ idUtilisateur, role });
      await get().fetchUsers();
      await get().fetchStats();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur changement role';
      set({ error: message });
      throw error;
    }
  },

  toggleActive: async (idUtilisateur: string, activate: boolean) => {
    try {
      await userAdminContainer.basculerActivationUseCase.execute({ idUtilisateur, activer: activate });
      await get().fetchUsers();
      await get().fetchStats();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur toggle active';
      set({ error: message });
      throw error;
    }
  },

  lockUser: async (idUtilisateur: string, dureeMinutes = 30) => {
    try {
      await userAdminContainer.verrouillerCompteUseCase.execute({ idUtilisateur, dureeMinutes });
      await get().fetchUsers();
      await get().fetchStats();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur verrouillage';
      set({ error: message });
      throw error;
    }
  },

  unlockUser: async (idUtilisateur: string) => {
    try {
      await userAdminContainer.deverrouillerCompteUseCase.execute({ idUtilisateur });
      await get().fetchUsers();
      await get().fetchStats();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur deverrouillage';
      set({ error: message });
      throw error;
    }
  },

  exportUsers: async () => {
    const state = get();
    try {
      const blob = await userAdminContainer.exporterUtilisateursUseCase.execute({
        role: state.filters.role || undefined,
        isActive: state.filters.isActive || undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisateurs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur export';
      set({ error: message });
    }
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  setFilters: (filters: UserFiltersState) => {
    set({ filters, currentPage: 1 });
  },

  resetFilters: () => {
    set({ filters: { ...initialFilters }, currentPage: 1 });
  },

  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),
});

export const useUserAdminStore = create<UserAdminState>()(storeImplementation);
